import { useState, useMemo, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowUpRight, ArrowDownLeft, ArrowRightLeft, CheckCircle2, Clock, XCircle,
  Search, Filter, Download, ChevronDown, ChevronRight, Building2,
  TrendingUp, TrendingDown, Landmark, Users, Banknote, ReceiptText,
  ArrowRight, Copy, AlertCircle
} from 'lucide-react';
import { formatINR, formatCurrencyDetailed } from '@/utils/currency';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { DashboardSkeleton, ErrorBanner } from '@/components/ui/loading-states';
import { toast } from 'sonner';
import { api } from '@/utils/api';

const TYPE_CONFIG = {
  EOI_PAYMENT:        { label: 'EOI Payment',        icon: ArrowDownLeft, color: 'text-emerald-400', bg: 'bg-emerald-400/10', direction: 'IN' },
  CAPITAL_PAYMENT:    { label: 'Capital Payment',    icon: ArrowDownLeft, color: 'text-blue-400',    bg: 'bg-blue-400/10',    direction: 'IN' },
  BUILDER_PAYMENT:    { label: 'Builder Payment',    icon: ArrowUpRight,  color: 'text-orange-400',  bg: 'bg-orange-400/10',  direction: 'OUT' },
  BROKER_COMMISSION:  { label: 'Commission',         icon: ArrowUpRight,  color: 'text-purple-400',  bg: 'bg-purple-400/10',  direction: 'OUT' },
  PLATFORM_FEE:       { label: 'Platform Fee',       icon: ArrowRightLeft,color: 'text-cyan-400',    bg: 'bg-cyan-400/10',    direction: 'INTERNAL' },
  REFUND:             { label: 'Refund',             icon: ArrowUpRight,  color: 'text-red-400',     bg: 'bg-red-400/10',     direction: 'OUT' },
  RENTAL_DISTRIBUTION:{ label: 'Rental Distribution',icon: ArrowUpRight,  color: 'text-teal-400',    bg: 'bg-teal-400/10',    direction: 'OUT' },
  EXIT_PAYOUT:        { label: 'Exit Payout',        icon: ArrowUpRight,  color: 'text-amber-400',   bg: 'bg-amber-400/10',   direction: 'OUT' },
};

const STATUS_CONFIG = {
  COMPLETED:  { label: 'Completed',  icon: CheckCircle2, color: 'text-emerald-400', bg: 'bg-emerald-400/10' },
  PENDING:    { label: 'Pending',    icon: Clock,        color: 'text-yellow-400',  bg: 'bg-yellow-400/10' },
  PROCESSING: { label: 'Processing', icon: Clock,        color: 'text-blue-400',    bg: 'bg-blue-400/10' },
  FAILED:     { label: 'Failed',     icon: XCircle,      color: 'text-red-400',     bg: 'bg-red-400/10' },
};

const FLOW_TABS = [
  { id: 'all',      label: 'All' },
  { id: 'IN',       label: 'Inflows' },
  { id: 'OUT',      label: 'Outflows' },
  { id: 'INTERNAL', label: 'Internal' },
];

function SummaryCard({ label, value, subtext, icon: Icon, accent }) {
  return (
    <div className="bg-white/[0.03] border border-white/[0.06] rounded-xl p-5">
      <div className="flex items-center justify-between mb-3">
        <p className="text-[11px] text-gray-500 uppercase tracking-widest">{label}</p>
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${accent || 'bg-white/5'}`}>
          <Icon className="w-4 h-4" />
        </div>
      </div>
      <p className="text-2xl font-mono text-white tracking-tight">{value}</p>
      {subtext && <p className="text-xs text-gray-500 mt-1.5">{subtext}</p>}
    </div>
  );
}

function VolumeChart({ data, onMonthClick, activeMonth }) {
  const maxVal = Math.max(...data.map(d => Math.max(d.inflow, d.outflow)));
  const [hovered, setHovered] = useState(null);

  // Y-axis ticks — 4 evenly spaced values
  const ticks = [0, 1, 2, 3].map(i => Math.round((maxVal * (3 - i)) / 3));

  return (
    <div className="bg-white/[0.03] border border-white/[0.06] rounded-xl p-5" data-testid="volume-chart">
      <div className="flex items-center justify-between mb-4">
        <p className="text-[11px] text-gray-500 uppercase tracking-widest">Monthly Volume</p>
        {activeMonth && (
          <button onClick={() => onMonthClick?.(null)} className="text-[10px] text-emerald-400 hover:underline">
            Clear filter
          </button>
        )}
      </div>

      <div className="flex h-40">
        {/* Y-axis labels */}
        <div className="flex flex-col justify-between pr-3 py-0.5 shrink-0 w-14">
          {ticks.map((t, i) => (
            <span key={i} className="text-[10px] text-gray-600 font-mono text-right leading-none">{formatINR(t)}</span>
          ))}
        </div>

        {/* Bars area */}
        <div className="flex-1 flex items-end gap-2 border-l border-b border-white/[0.06] pl-2 pb-1 relative">
          {/* Horizontal grid lines */}
          {ticks.map((t, i) => (
            <div
              key={`grid-${i}`}
              className="absolute left-0 right-0 border-t border-dashed border-white/[0.04]"
              style={{ bottom: `${(t / maxVal) * 100}%` }}
            />
          ))}

          {data.map((d) => {
            const isHovered = hovered === d.month;
            const isActive = activeMonth === d.month;
            const barH = 136; // approx px for the bar area

            return (
              <div
                key={d.month}
                className={`flex-1 flex flex-col items-center gap-1 relative cursor-pointer group ${isActive ? 'z-10' : ''}`}
                onMouseEnter={() => setHovered(d.month)}
                onMouseLeave={() => setHovered(null)}
                onClick={() => onMonthClick?.(isActive ? null : d.month)}
              >
                {/* Tooltip on hover */}
                <AnimatePresence>
                  {isHovered && (
                    <motion.div
                      initial={{ opacity: 0, y: 4 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 4 }}
                      className="absolute -top-16 left-1/2 -translate-x-1/2 bg-gray-900 border border-white/10 rounded-lg px-3 py-2 z-20 whitespace-nowrap shadow-xl"
                    >
                      <p className="text-[10px] text-gray-400 mb-1">{d.month}</p>
                      <p className="text-[11px] text-emerald-400 font-mono">In: {formatINR(d.inflow)}</p>
                      <p className="text-[11px] text-orange-400 font-mono">Out: {formatINR(d.outflow)}</p>
                      <p className={`text-[11px] font-mono ${d.net >= 0 ? 'text-white' : 'text-red-400'}`}>Net: {d.net >= 0 ? '+' : ''}{formatINR(d.net)}</p>
                      <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-gray-900 border-b border-r border-white/10 rotate-45" />
                    </motion.div>
                  )}
                </AnimatePresence>

                <div className={`w-full flex gap-0.5 items-end justify-center transition-all duration-200 ${isActive ? 'opacity-100' : activeMonth ? 'opacity-40' : 'opacity-100'}`} style={{ height: barH }}>
                  <div
                    className={`w-[40%] rounded-t transition-all duration-200 ${isHovered ? 'bg-emerald-400' : 'bg-emerald-500/60'}`}
                    style={{ height: `${(d.inflow / maxVal) * 100}%`, minHeight: d.inflow > 0 ? '4px' : 0 }}
                  />
                  <div
                    className={`w-[40%] rounded-t transition-all duration-200 ${isHovered ? 'bg-orange-300' : 'bg-orange-400/60'}`}
                    style={{ height: `${(d.outflow / maxVal) * 100}%`, minHeight: d.outflow > 0 ? '4px' : 0 }}
                  />
                </div>
                <span className={`text-[10px] whitespace-nowrap transition-colors ${isActive ? 'text-emerald-400' : 'text-gray-600'}`}>
                  {d.month.replace(' 20', ' ')}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      <div className="flex items-center gap-4 mt-3 pt-3 border-t border-white/5">
        <span className="flex items-center gap-1.5 text-[10px] text-gray-500">
          <span className="w-2 h-2 rounded-sm bg-emerald-500/60" />Inflow
        </span>
        <span className="flex items-center gap-1.5 text-[10px] text-gray-500">
          <span className="w-2 h-2 rounded-sm bg-orange-400/60" />Outflow
        </span>
        <span className="text-[10px] text-gray-600 ml-auto">Click a month to filter table</span>
      </div>
    </div>
  );
}

function CategoryBreakdown({ categories, subtitle }) {
  const sorted = Object.entries(categories)
    .filter(([, v]) => v.count > 0)
    .sort((a, b) => b[1].amount - a[1].amount);
  const totalAmount = sorted.reduce((sum, [, v]) => sum + v.amount, 0);

  return (
    <div className="bg-white/[0.03] border border-white/[0.06] rounded-xl p-5">
      <p className="text-[11px] text-gray-500 uppercase tracking-widest mb-1">By Category</p>
      {subtitle && <p className="text-[10px] text-emerald-400 mb-3">{subtitle}</p>}
      {!subtitle && <div className="mb-3" />}
      {sorted.length === 0 ? (
        <p className="text-xs text-gray-600">No transactions in this period</p>
      ) : (
        <div className="space-y-3">
          {sorted.map(([key, val]) => {
          const cfg = TYPE_CONFIG[key];
          const pct = totalAmount > 0 ? ((val.amount / totalAmount) * 100) : 0;
          return (
            <div key={key}>
              <div className="flex items-center justify-between mb-1">
                <span className={`text-xs ${cfg?.color || 'text-gray-400'}`}>{val.label}</span>
                <span className="text-xs font-mono text-gray-400">{formatINR(val.amount)} ({val.count})</span>
              </div>
              <div className="w-full h-1.5 bg-white/5 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all ${cfg?.color?.replace('text-', 'bg-') || 'bg-gray-500'}`}
                  style={{ width: `${pct}%`, opacity: 0.7 }}
                />
              </div>
            </div>
          );
        })}
      </div>
      )}
    </div>
  );
}

function TransactionRow({ tx, isExpanded, onToggle }) {
  const cfg = TYPE_CONFIG[tx.type] || TYPE_CONFIG['EOI_PAYMENT'];
  const statusCfg = STATUS_CONFIG[tx.status] || STATUS_CONFIG['PENDING'];
  const TypeIcon = cfg.icon;
  const StatusIcon = statusCfg.icon;
  const isInflow = cfg.direction === 'IN';

  return (
    <>
      <motion.div
        layout
        onClick={onToggle}
        className="grid grid-cols-12 gap-3 px-5 py-3.5 hover:bg-white/[0.03] transition-colors items-center cursor-pointer group"
        data-testid={`txn-row-${tx.id}`}
      >
        {/* Expand / ID */}
        <div className="col-span-2 flex items-center gap-2">
          <ChevronRight className={`w-3.5 h-3.5 text-gray-600 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
          <div className="min-w-0 max-w-[120px]">
            <p 
              className="text-xs font-mono text-white group-hover:text-emerald-400 transition-colors truncate"
              data-testid="transaction-id"
            >
              {tx.id}
            </p>
            <p className="text-[10px] text-gray-600">{tx.date}</p>
          </div>
        </div>

        {/* Type */}
        <div className="col-span-2">
          <span className={`inline-flex items-center gap-1.5 px-2 py-1 text-[11px] rounded ${cfg.color} ${cfg.bg}`}>
            <TypeIcon className="w-3 h-3" />
            {cfg.label}
          </span>
        </div>

        {/* From → To */}
        <div className="col-span-3 flex items-center gap-1.5 min-w-0">
          <span className="text-xs text-white truncate max-w-[100px]" title={tx.from.name}>{tx.from.name}</span>
          <ArrowRight className="w-3 h-3 text-gray-600 flex-shrink-0" />
          <span className="text-xs text-gray-400 truncate max-w-[100px]" title={tx.to.name}>{tx.to.name}</span>
        </div>

        {/* Property / Unit */}
        <div className="col-span-2">
          <p className="text-xs text-white truncate">{tx.property}</p>
          <p className="text-[10px] text-gray-600">{tx.unit}</p>
        </div>

        {/* Amount */}
        <div className="col-span-2 text-right">
          <p className={`font-mono text-sm ${isInflow ? 'text-emerald-400' : cfg.direction === 'INTERNAL' ? 'text-cyan-400' : 'text-white'}`}>
            {isInflow ? '+' : cfg.direction === 'OUT' ? '-' : ''}{formatINR(tx.amount)}
          </p>
        </div>

        {/* Status */}
        <div className="col-span-1">
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] rounded ${statusCfg.color} ${statusCfg.bg}`}>
            <StatusIcon className="w-2.5 h-2.5" />
            {statusCfg.label}
          </span>
        </div>
      </motion.div>

      {/* Expanded Detail */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="px-5 py-4 bg-white/[0.02] border-t border-white/5 ml-10">
              <div className="grid grid-cols-4 gap-6 text-xs">
                <div>
                  <p className="text-gray-600 mb-1">Transaction ID</p>
                  <div className="flex items-center gap-1.5">
                    <p className="text-white font-mono text-[11px] break-all">{tx.id}</p>
                    <button
                      onClick={(e) => { e.stopPropagation(); navigator.clipboard.writeText(tx.id); toast.success('Copied'); }}
                      className="text-gray-600 hover:text-white flex-shrink-0"
                    >
                      <Copy className="w-3 h-3" />
                    </button>
                  </div>
                </div>
                <div>
                  <p className="text-gray-600 mb-1">LLP</p>
                  <p className="text-white">{tx.llp || '—'}</p>
                </div>
                <div>
                  <p className="text-gray-600 mb-1">Payment Reference</p>
                  <div className="flex items-center gap-1.5">
                    <p className="text-white font-mono">{tx.reference || 'Awaiting'}</p>
                    {tx.reference && (
                      <button
                        onClick={(e) => { e.stopPropagation(); navigator.clipboard.writeText(tx.reference); toast.success('Copied'); }}
                        className="text-gray-600 hover:text-white"
                      >
                        <Copy className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                </div>
                <div>
                  <p className="text-gray-600 mb-1">
                    {tx.type === 'BROKER_COMMISSION' ? 'Invoice Ref' : tx.type === 'BUILDER_PAYMENT' ? 'Milestone' : tx.type === 'REFUND' ? 'Reason' : tx.type === 'PLATFORM_FEE' ? 'Fee Type' : 'Broker'}
                  </p>
                  <p className="text-white">{tx.invoiceRef || tx.milestone || tx.reason || tx.feeType || tx.broker || '—'}</p>
                </div>
              </div>
              <div className="grid grid-cols-4 gap-6 text-xs mt-4">
                <div>
                  <p className="text-gray-600 mb-1">Full Amount</p>
                  <p className="text-white font-mono">{formatCurrencyDetailed(tx.amount)}</p>
                </div>
              </div>
              {tx.status === 'PENDING' && (
                <div className="mt-3 pt-3 border-t border-white/5 flex items-center gap-2">
                  <AlertCircle className="w-3.5 h-3.5 text-yellow-400" />
                  <span className="text-[11px] text-yellow-400">Awaiting confirmation — no payment reference recorded yet</span>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

/** Transform API response to match component's expected data shape */
function transformAPIData(apiData) {
  const { transactions = [], summary = {}, chartData = [], typeBreakdown = {} } = apiData;

  const INFLOW = new Set(['EOI_PAYMENT', 'CAPITAL_PAYMENT', 'INVESTOR_PAYMENT', 'TRANCHE_PAYMENT']);
  const OUTFLOW = new Set(['BUILDER_PAYMENT', 'BROKER_COMMISSION', 'BROKER_COMMISSION_PAYOUT', 'COMMISSION_PAYOUT', 'REFUND', 'DEVELOPER_PAYMENT']);

  const txns = transactions.map(t => ({
    id: t.transactionId || t.paymentId || t.ledgerEntryId || `TXN_${Math.random().toString(36).slice(2, 8).toUpperCase()}`,
    type: t.type || 'OTHER',
    amount: t.amount || 0,
    from: { name: INFLOW.has(t.type) ? (t.investorName || 'Investor') : (t.unitName || 'LLP Account'), type: INFLOW.has(t.type) ? 'investor' : 'llp' },
    to: { name: INFLOW.has(t.type) ? (t.unitName || 'Escrow') : (t.investorName || t.brokerName || 'Recipient'), type: INFLOW.has(t.type) ? 'escrow' : 'investor' },
    status: (t.status || 'COMPLETED').toUpperCase(),
    date: (t.createdAt || t.date || '').slice(0, 10),
    property: t.projectName || t.property || '',
    unit: t.unitName || t.unitId || '',
    llp: t.llpName || '',
    reference: t.reference || t.bankRef || '',
    broker: t.brokerName || '',
  }));

  const totalVolume = summary.totalInflow + summary.totalOutflow;
  const totalCount = summary.totalTransactions || txns.length;

  // Category breakdown
  const catBreakdown = {};
  for (const [key, cfg] of Object.entries(TYPE_CONFIG)) {
    const matching = txns.filter(t => t.type === key);
    catBreakdown[key] = {
      count: matching.length,
      amount: matching.reduce((s, t) => s + t.amount, 0),
      label: cfg.label,
      direction: cfg.direction,
    };
  }

  // Monthly volume
  const monthlyVolume = chartData.map(m => {
    const d = new Date(m.month + '-01');
    const label = d.toLocaleString('en-US', { month: 'short', year: 'numeric' });
    return { month: label, inflow: m.inflow, outflow: m.outflow, net: m.net };
  });
  // Ensure at least 6 months of data for chart
  if (monthlyVolume.length === 0) {
    const now = new Date();
    for (let i = 5; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      monthlyVolume.push({ month: d.toLocaleString('en-US', { month: 'short', year: 'numeric' }), inflow: 0, outflow: 0, net: 0 });
    }
  }

  return {
    summary: {
      totalVolume,
      totalInflow: summary.totalInflow || 0,
      totalOutflow: summary.totalOutflow || 0,
      pendingAmount: summary.pendingAmount || 0,
      transactionCount: totalCount,
      avgTransactionSize: totalCount ? Math.round(totalVolume / totalCount) : 0,
      monthOverMonthGrowth: 0,
    },
    monthlyVolume,
    categoryBreakdown: catBreakdown,
    transactions: txns,
  };
}

export default function AdminTransactionsTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [flowTab, setFlowTab] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [propertyFilter, setPropertyFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedTxn, setExpandedTxn] = useState(null);
  const [sortField, setSortField] = useState('date');
  const [sortDir, setSortDir] = useState('desc');
  const [monthFilter, setMonthFilter] = useState(null);

  const fetchTransactions = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get('/admin/transactions?limit=200');
      setData(transformAPIData(res.data));
    } catch (e) {
      console.error('Failed to load transactions:', e);
      setError('Failed to load transactions. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchTransactions(); }, [fetchTransactions]);

  const properties = useMemo(() => {
    if (!data) return [];
    const set = new Set(data.transactions.map(t => t.property));
    return Array.from(set);
  }, [data]);

  const filteredTransactions = useMemo(() => {
    if (!data) return [];
    let list = [...data.transactions];

    // Flow tab
    if (flowTab !== 'all') {
      list = list.filter(tx => {
        const cfg = TYPE_CONFIG[tx.type];
        return cfg?.direction === flowTab;
      });
    }
    // Type filter
    if (typeFilter !== 'all') list = list.filter(tx => tx.type === typeFilter);
    // Status filter
    if (statusFilter !== 'all') list = list.filter(tx => tx.status === statusFilter);
    // Property filter
    if (propertyFilter !== 'all') list = list.filter(tx => tx.property === propertyFilter);
    // Month filter (from chart click)
    if (monthFilter) {
      list = list.filter(tx => {
        const d = new Date(tx.date);
        const m = d.toLocaleString('en-US', { month: 'short', year: 'numeric' });
        return m === monthFilter;
      });
    }
    // Search
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      list = list.filter(tx =>
        tx.id.toLowerCase().includes(q) ||
        tx.from.name.toLowerCase().includes(q) ||
        tx.to.name.toLowerCase().includes(q) ||
        tx.property.toLowerCase().includes(q) ||
        tx.unit.toLowerCase().includes(q) ||
        (tx.reference || '').toLowerCase().includes(q)
      );
    }
    // Sort
    list.sort((a, b) => {
      if (sortField === 'date') return sortDir === 'desc' ? b.date.localeCompare(a.date) : a.date.localeCompare(b.date);
      if (sortField === 'amount') return sortDir === 'desc' ? b.amount - a.amount : a.amount - b.amount;
      return 0;
    });
    return list;
  }, [data, flowTab, typeFilter, statusFilter, propertyFilter, monthFilter, searchQuery, sortField, sortDir]);

  const pendingCount = data ? data.transactions.filter(t => t.status === 'PENDING').length : 0;

  // Build category breakdown from month-filtered transactions (chart + table stay synced)
  const dynamicCategories = useMemo(() => {
    if (!data) return {};
    let txns = data.transactions;
    if (monthFilter) {
      txns = txns.filter(tx => {
        const d = new Date(tx.date);
        return d.toLocaleString('en-US', { month: 'short', year: 'numeric' }) === monthFilter;
      });
    }
    const cats = {};
    for (const [key, base] of Object.entries(data.categoryBreakdown)) {
      cats[key] = { ...base, count: 0, amount: 0 };
    }
    txns.forEach(tx => {
      if (cats[tx.type]) { cats[tx.type].count += 1; cats[tx.type].amount += tx.amount; }
    });
    return cats;
  }, [data, monthFilter]);

  if (loading) return <div className="p-8 max-w-[1440px]"><DashboardSkeleton cards={5} rows={8} cols={6} /></div>;
  if (error) return <div className="p-8 max-w-[1440px]"><ErrorBanner message={error} onRetry={fetchTransactions} /></div>;
  if (!data) return null;

  const handleExport = () => {
    const headers = ['ID', 'Date', 'Type', 'From', 'To', 'Property', 'Unit', 'LLP', 'Amount', 'Status', 'Reference'];
    const rows = filteredTransactions.map(tx => [
      tx.id, tx.date, tx.type, tx.from.name, tx.to.name, tx.property, tx.unit, tx.llp, tx.amount, tx.status, tx.reference || ''
    ]);
    const csv = [headers, ...rows].map(r => r.map(v => `"${v}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `transactions_${new Date().toISOString().slice(0, 10)}.csv`; a.click();
    URL.revokeObjectURL(url);
    toast.success('CSV exported');
  };

  const toggleSort = (field) => {
    if (sortField === field) setSortDir(d => d === 'desc' ? 'asc' : 'desc');
    else { setSortField(field); setSortDir('desc'); }
  };

  return (
    <div className="p-8 max-w-[1440px]" data-testid="transactions-tab">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-light text-white mb-1" data-testid="transactions-heading">All Transactions</h1>
          <p className="text-sm text-gray-500">Platform-wide money movement across all LLPs and properties</p>
        </div>
        <Button
          onClick={handleExport}
          variant="outline"
          className="border-white/10 text-white hover:bg-white/5 text-xs"
          data-testid="export-csv-btn"
        >
          <Download className="w-3.5 h-3.5 mr-1.5" />Export CSV
        </Button>
      </div>

      {/* Summary Row */}
      <div className="grid grid-cols-5 gap-4 mb-6" data-testid="summary-cards">
        <SummaryCard label="Total Volume" value={formatINR(data.summary.totalVolume)} subtext={`${data.summary.transactionCount} transactions`} icon={Banknote} accent="bg-white/5 text-white" />
        <SummaryCard label="Inflows" value={formatINR(data.summary.totalInflow)} subtext="EOI + Capital payments" icon={TrendingUp} accent="bg-emerald-500/10 text-emerald-400" />
        <SummaryCard label="Outflows" value={formatINR(data.summary.totalOutflow)} subtext="Builder + Commission + Refunds" icon={TrendingDown} accent="bg-orange-400/10 text-orange-400" />
        <SummaryCard label="Pending" value={formatINR(data.summary.pendingAmount)} subtext={`${pendingCount} transactions`} icon={Clock} accent="bg-yellow-400/10 text-yellow-400" />
        <SummaryCard label="MoM Growth" value={`+${data.summary.monthOverMonthGrowth}%`} subtext="vs. previous month" icon={TrendingUp} accent="bg-blue-400/10 text-blue-400" />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="col-span-2">
          <VolumeChart data={data.monthlyVolume} onMonthClick={setMonthFilter} activeMonth={monthFilter} />
        </div>
        <CategoryBreakdown categories={dynamicCategories} subtitle={monthFilter || null} />
      </div>

      {/* Flow Tabs + Filters */}
      <div className="flex items-center justify-between mb-4 gap-4">
        <div className="flex items-center gap-1 bg-white/[0.03] border border-white/[0.06] rounded-lg p-1" data-testid="flow-tabs">
          {FLOW_TABS.map(tab => (
            <button
              key={tab.id}
              onClick={() => setFlowTab(tab.id)}
              className={`px-3.5 py-1.5 text-xs rounded-md transition-all ${
                flowTab === tab.id ? 'bg-white/10 text-white' : 'text-gray-500 hover:text-gray-300'
              }`}
              data-testid={`flow-tab-${tab.id}`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-2.5 flex-1 justify-end">
          <div className="relative max-w-[240px] w-full">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-600" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search ID, name, property..."
              className="pl-9 h-8 text-xs bg-white/[0.03] border-white/[0.06] text-white placeholder:text-gray-600"
              data-testid="search-input"
            />
          </div>

          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="h-8 bg-white/[0.03] border border-white/[0.06] text-gray-400 px-2.5 text-xs rounded-md"
            data-testid="type-filter"
          >
            <option value="all">All Types</option>
            {Object.entries(TYPE_CONFIG).map(([key, cfg]) => (
              <option key={key} value={key}>{cfg.label}</option>
            ))}
          </select>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="h-8 bg-white/[0.03] border border-white/[0.06] text-gray-400 px-2.5 text-xs rounded-md"
            data-testid="status-filter"
          >
            <option value="all">All Status</option>
            <option value="COMPLETED">Completed</option>
            <option value="PENDING">Pending</option>
            <option value="FAILED">Failed</option>
          </select>

          <select
            value={propertyFilter}
            onChange={(e) => setPropertyFilter(e.target.value)}
            className="h-8 bg-white/[0.03] border border-white/[0.06] text-gray-400 px-2.5 text-xs rounded-md"
            data-testid="property-filter"
          >
            <option value="all">All Properties</option>
            {properties.map(p => <option key={p} value={p}>{p}</option>)}
          </select>
        </div>
      </div>

      {/* Transaction Table */}
      <div className="border border-white/[0.06] rounded-xl overflow-hidden" data-testid="transactions-table">
        {/* Table Header */}
        <div className="grid grid-cols-12 gap-3 px-5 py-3 bg-white/[0.03] border-b border-white/[0.06] text-[10px] text-gray-600 uppercase tracking-wider">
          <div className="col-span-2 flex items-center gap-1 cursor-pointer hover:text-gray-400" onClick={() => toggleSort('date')}>
            ID / Date {sortField === 'date' && <ChevronDown className={`w-3 h-3 transition-transform ${sortDir === 'asc' ? 'rotate-180' : ''}`} />}
          </div>
          <div className="col-span-2">Type</div>
          <div className="col-span-3">From / To</div>
          <div className="col-span-2">Property / Unit</div>
          <div className="col-span-2 text-right flex items-center gap-1 justify-end cursor-pointer hover:text-gray-400" onClick={() => toggleSort('amount')}>
            Amount {sortField === 'amount' && <ChevronDown className={`w-3 h-3 transition-transform ${sortDir === 'asc' ? 'rotate-180' : ''}`} />}
          </div>
          <div className="col-span-1">Status</div>
        </div>

        {/* Rows */}
        {filteredTransactions.length === 0 ? (
          <div className="py-16 text-center" data-testid="empty-state">
            <ReceiptText className="w-10 h-10 text-gray-700 mx-auto mb-3" />
            <p className="text-gray-500 text-sm">No transactions match your filters</p>
          </div>
        ) : (
          <div className="divide-y divide-white/[0.04]">
            {filteredTransactions.map((tx) => (
              <TransactionRow
                key={tx.id}
                tx={tx}
                isExpanded={expandedTxn === tx.id}
                onToggle={() => setExpandedTxn(expandedTxn === tx.id ? null : tx.id)}
              />
            ))}
          </div>
        )}

        {/* Footer */}
        <div className="px-5 py-3 bg-white/[0.02] border-t border-white/[0.06] flex items-center justify-between">
          <p className="text-[11px] text-gray-600">
            Showing {filteredTransactions.length} of {data.transactions.length} transactions
          </p>
          <p className="text-[11px] text-gray-600">
            Filtered Volume: <span className="text-white font-mono">{formatINR(filteredTransactions.reduce((s, t) => s + t.amount, 0))}</span>
          </p>
        </div>
      </div>
    </div>
  );
}
